import { z } from "zod";
import type { MerchantConfig } from "./types";
import { ALL_CATEGORIES } from "../categories";

// Source type enum
const sourceTypeSchema = z.enum([
  "FLYER_LANDING",
  "FLYER_PDF",
  "GIFT_CARD_PAGE",
  "PROMO_PAGE",
  "TERMS_PAGE",
  "BONUS_PORTAL",
  "LOCATOR_ONLY",
]);

// Scope enum
const scopeSchema = z.enum(["ON", "CA", "UNKNOWN"]);

// Parser hint enum
const parserHintSchema = z.enum(["html", "pdf", "image", "jsRequired"]);

// Source config schema
const sourceConfigSchema = z.object({
  id: z.string().min(1),
  merchantId: z.string().min(1),
  label: z.string().min(1),
  url: z.string().url(),
  sourceType: sourceTypeSchema,
  scope: scopeSchema,
  official: z.boolean(),
  priority: z.number().int().positive(),
  parserHint: parserHintSchema.optional(),
  notes: z.string().optional(),
  health: z.object({
    lastCheckedAt: z.string().optional(),
    ok: z.boolean().optional(),
    httpStatus: z.number().optional(),
    contentType: z.string().optional(),
    error: z.string().optional(),
  }).optional(),
});

// Category enum from the 7-category system
const categoryKeySchema = z.enum(ALL_CATEGORIES as [string, ...string[]]);

// Full merchant config schema
export const merchantConfigSchema = z.object({
  id: z.string().min(1),
  displayName: z.string().min(1),
  categoryKey: categoryKeySchema,
  merchantDomainsKey: z.string().optional(),
  sources: z.array(sourceConfigSchema),
});

// Extra source schema
export const extraSourceSchema = z.object({
  name: z.string().min(1),
  url: z.string().url(),
  type: z.enum(["forum", "aggregator", "flyer_aggregator"]),
  priority: z.number().int().positive(),
});

// Validate a single merchant config
export function validateMerchantConfig(config: unknown): MerchantConfig {
  return merchantConfigSchema.parse(config) as MerchantConfig;
}

// Validate all merchants (call on startup/build)
export function validateAllMerchants(
  merchants: Record<string, unknown>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  for (const [id, config] of Object.entries(merchants)) {
    try {
      merchantConfigSchema.parse(config);

      // Additional validation: id should match the key
      if ((config as MerchantConfig).id !== id) {
        errors.push(`Merchant ${id}: id field doesn't match key`);
      }
      
      // Validate source IDs are unique within merchant
      const merchant = config as MerchantConfig;
      const sourceIds = new Set<string>();
      for (const source of merchant.sources) {
        if (sourceIds.has(source.id)) {
          errors.push(`Merchant ${id}: duplicate source id "${source.id}"`);
        }
        sourceIds.add(source.id);
        
        // Validate source merchantId matches
        if (source.merchantId !== id) {
          errors.push(`Merchant ${id}: source "${source.id}" has wrong merchantId`);
        }
      }
    } catch (e) {
      if (e instanceof z.ZodError) {
        errors.push(`Merchant ${id}: ${e.errors.map((err) => err.message).join(", ")}`);
      } else {
        errors.push(`Merchant ${id}: Unknown validation error`);
      }
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
